import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SalesPersonCustomersMapToken, SalesPersonInspectorsMapToken } from '@sleed/bizcommerce-api';
import { salesPersonCustomersMapApi, salesPersonInspectorsMapApi } from '~/lib/api/client';

/**
 * Per-sales-person (or per-inspector) link sub-views. `GetInspectors`/`GetCustomers` return the
 * full candidate list, each row carrying a link flag (`iil` / `icl`); a non-linked row has a
 * synthetic negative id. Toggling the flag links/unlinks via SaveSingle / SaveLinkedCustomers
 * (parity with the legacy SalesPersonLinked{Inspectors,Customers} grids).
 */

export const salesPersonLinkKeys = {
	inspectors: (sid: number) => ['sales-person-links', 'inspectors', sid] as const,
	customers: (sid: number) => ['sales-person-links', 'customers', sid] as const
};

export function useLinkedInspectors(sid: number | null) {
	return useQuery<SalesPersonInspectorsMapToken[]>({
		queryKey: salesPersonLinkKeys.inspectors(sid ?? 0),
		queryFn: async () => (await salesPersonInspectorsMapApi.getInspectors(sid ?? 0)).data.items ?? [],
		enabled: sid != null && sid > 0
	});
}

export function useToggleLinkedInspector(sid: number | null) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ row, linked }: { row: SalesPersonInspectorsMapToken; linked: boolean }) =>
			(await salesPersonInspectorsMapApi.saveSingle({ ...row, iil: linked })).data,
		onSuccess: () => qc.invalidateQueries({ queryKey: salesPersonLinkKeys.inspectors(sid ?? 0) })
	});
}

export function useLinkedCustomers(sid: number | null) {
	return useQuery<SalesPersonCustomersMapToken[]>({
		queryKey: salesPersonLinkKeys.customers(sid ?? 0),
		queryFn: async () => (await salesPersonCustomersMapApi.getCustomers(sid ?? 0)).data.items ?? [],
		enabled: sid != null && sid > 0
	});
}

export function useToggleLinkedCustomer(sid: number | null) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ row, linked }: { row: SalesPersonCustomersMapToken; linked: boolean }) =>
			(await salesPersonCustomersMapApi.saveLinkedCustomers({ ...row, icl: linked })).data,
		onSuccess: () => qc.invalidateQueries({ queryKey: salesPersonLinkKeys.customers(sid ?? 0) })
	});
}
